// src/pages/ManageHostPermissions.tsx - Updated Host Game Access Management
import React, {useEffect, useState} from 'react';
import {useParams, useNavigate, Link} from 'react-router-dom';
import {
    CheckCircle,
    XCircle,
    GamepadIcon,
    ArrowLeft,
    User,
    Mail,
    Building2,
    School,
    Calendar,
    AlertTriangle,
    Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    useHostPermissions,
    useGameInstances,
    useGrantGameAccess,
    useRevokeGameAccess,
    GameInstance,
    HostGamePermission, GrantGameAccessResponse, GrantGameAccessInput, RevokeGameAccessResponse, RevokeGameAccessInput
} from '@/hooks/useHostPermissions';
import {supabase} from '@/services/supabase';
import {Button, LoadingSpinner, Badge} from '@/components/ui';
import type {GameHost, Account, Organization} from '@/types';
import {formatDate} from "@/utils/formatters";
import {UseMutationResult} from "@tanstack/react-query";

// =====================================================
// TYPE DEFINITIONS
// =====================================================

interface GameRowData {
    game: GameInstance;
    permission: HostGamePermission | null;
    hasAccess: boolean;
    isActive: boolean;
    expirationText: string;
}

interface GameHostWithRelations extends GameHost {
    accounts: Account & {
        organizations: Organization;
    };
}

// =====================================================
// MAIN COMPONENT
// =====================================================

export const ManageHostPermissions: React.FC = () => {
    const {userId} = useParams<{ userId: string }>();
    const navigate = useNavigate();

    const {data: gameInstances = [], isLoading: gamesLoading} = useGameInstances();
    const {data: permissions = [], isLoading: permissionsLoading} = useHostPermissions(userId || '');
    const grantAccess: UseMutationResult<GrantGameAccessResponse, Error, GrantGameAccessInput> = useGrantGameAccess();
    const revokeAccess: UseMutationResult<RevokeGameAccessResponse, Error, RevokeGameAccessInput> = useRevokeGameAccess();

    const [loading, setLoading] = useState<boolean>(true);
    const [selectedHost, setSelectedHost] = useState<GameHostWithRelations | null>(null);
    const [gameExpirations, setGameExpirations] = useState<Record<string, string>>({});

    // =====================================================
    // LOAD HOST AND PERMISSIONS
    // =====================================================

    useEffect(() => {
        if (!userId) {
            navigate('/game-hosts');
            return;
        }

        loadHostData(); // Keep this one - it loads the selected host info
    }, [userId]);

    const loadHostData = async (): Promise<void> => {
        try {
            setLoading(true);

            const {data: hostData, error} = await supabase
                .from('game_hosts')
                .select(`
                *,
                accounts!inner(
                    *,
                    organizations!inner(*)
                )
            `)
                .eq('id', userId)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    toast.error('Game host not found');
                    navigate('/game-hosts');
                    return;
                }
                throw error;
            }

            setSelectedHost(hostData as GameHostWithRelations);
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            console.error('Error loading host data:', error);
            toast.error(`Failed to load host data: ${errorMessage}`);
            navigate('/game-hosts');
        } finally {
            setLoading(false);
        }
    };

    // =====================================================
    // PERMISSION MANAGEMENT
    // =====================================================

    const handleGrantAccess = async (gameInstanceId: string): Promise<void> => {
        if (!userId) return;

        const gameName: string | undefined = gameInstances.find(g => g.id === gameInstanceId)?.name;
        if (!gameName) return;

        const expirationDate: string = gameExpirations[gameInstanceId];
        const expiresInDays: number | undefined = expirationDate
            ? Math.ceil((new Date(expirationDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
            : undefined;

        try {
            await grantAccess.mutateAsync({
                userId,
                gameName,
                expiresInDays,
            });
        } catch (error) {
            // Error is handled in the hook
            console.error('Grant access error:', error);
        }
    };

    const handleRevokeAccess = async (gameInstanceId: string): Promise<void> => {
        if (!userId) return;

        // Find permission by game_instance_id
        const permission: HostGamePermission | undefined = permissions.find(p => p.game_instance_id === gameInstanceId);
        if (!permission) return;

        try {
            await revokeAccess.mutateAsync({
                permissionId: permission.id,
                userId
            });
        } catch (error) {
            // Error is handled in the hook
            console.error('Revoke access error:', error);
        }
    };

    const handleExpirationChange = (gameInstanceId: string, date: string): void => {
        setGameExpirations(prev => ({
            ...prev,
            [gameInstanceId]: date
        }));
    };

    // =====================================================
    // HELPER FUNCTIONS
    // =====================================================

    const getGameRowData = (): GameRowData[] => {
        return gameInstances.map((game: GameInstance): GameRowData => {
            // Use game_instance_id to match with game.id
            const permission: HostGamePermission | null = permissions.find(p => p.game_instance_id === game.id) || null;
            const hasAccess: boolean = !!permission;
            const isActive: boolean = hasAccess && (!permission?.expires_at || new Date(permission.expires_at) > new Date());

            let expirationText: string = 'No expiration';
            if (permission?.expires_at) {
                const expDate: Date = new Date(permission.expires_at);
                const now: Date = new Date();
                if (expDate < now) {
                    expirationText = `Expired ${formatDate(permission.expires_at) || 'recently'}`;
                } else {
                    expirationText = `Expires ${formatDate(permission.expires_at) || 'soon'}`;
                }
            }

            return {
                game,
                permission,
                hasAccess,
                isActive,
                expirationText
            };
        });
    };

    const getAccessBadge = (rowData: GameRowData) => {
        if (!rowData.hasAccess) {
            return <Badge variant="default">No Access</Badge>;
        }

        if (!rowData.isActive) {
            return <Badge variant="danger">Expired</Badge>;
        }

        return <Badge variant="success">Active</Badge>;
    };

    // =====================================================
    // RENDER LOADING STATE
    // =====================================================

    if (loading || gamesLoading || permissionsLoading) {
        return (
            <div className="p-6">
                <LoadingSpinner size="lg"/>
            </div>
        );
    }

    if (!selectedHost) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-5 w-5 text-red-500"/>
                        <p className="text-red-700">Game host not found</p>
                    </div>
                </div>
            </div>
        );
    }

    const gameRowsData = getGameRowData();

    // =====================================================
    // MAIN RENDER
    // =====================================================

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Link to="/game-hosts" className="hover:text-gray-700 flex items-center space-x-1">
                        <ArrowLeft className="h-4 w-4"/>
                        <span>Back to Game Hosts</span>
                    </Link>
                </div>
            </div>

            {/* Host Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                        <div className="p-3 bg-purple-100 rounded-lg">
                            <User className="h-6 w-6 text-purple-600"/>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {selectedHost.first_name} {selectedHost.last_name}
                            </h1>
                            <div className="mt-2 space-y-1">
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <Mail className="h-4 w-4"/>
                                    <span>{selectedHost.email}</span>
                                </div>
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <School className="h-4 w-4"/>
                                    <span>{selectedHost.accounts.name}</span>
                                </div>
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <Building2 className="h-4 w-4"/>
                                    <span>{selectedHost.accounts.organizations.name}, {selectedHost.accounts.organizations.state}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Badge variant={selectedHost.status === 'active' ? 'success' : 'default'}>
                            {selectedHost.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                    </div>
                </div>
            </div>

            {/* Game Access Management */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Game Access Permissions</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Manage which games this host can access and create sessions for
                            </p>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <Info className="h-4 w-4"/>
                            <span>{gameRowsData.filter(row => row.isActive).length} of {gameInstances.length} games accessible</span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="text-left py-4 px-6 font-semibold text-gray-700">Game</th>
                            <th className="text-left py-4 px-6 font-semibold text-gray-700">Access Status</th>
                            <th className="text-left py-4 px-6 font-semibold text-gray-700">Expiration</th>
                            <th className="text-left py-4 px-6 font-semibold text-gray-700">Set Expiration</th>
                            <th className="text-left py-4 px-6 font-semibold text-gray-700">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {gameRowsData.map((rowData) => (
                            <tr key={rowData.game.id} className="border-b border-gray-100 hover:bg-gray-50">
                                {/* Game Name */}
                                <td className="py-4 px-6">
                                    <div className="flex items-center space-x-3">
                                        <GamepadIcon className="h-5 w-5 text-blue-500"/>
                                        <div>
                                            <p className="font-medium text-gray-900">{rowData.game.display_name}</p>
                                            <p className="text-sm text-gray-500">{rowData.game.name}</p>
                                        </div>
                                    </div>
                                </td>

                                {/* Access Status */}
                                <td className="py-4 px-6">
                                    {getAccessBadge(rowData)}
                                </td>

                                {/* Current Expiration */}
                                <td className="py-4 px-6">
                                    <div className="flex items-center space-x-1">
                                        <Calendar className="h-4 w-4 text-gray-400"/>
                                        <span className={`text-sm ${
                                            rowData.permission?.expires_at && new Date(rowData.permission.expires_at) < new Date()
                                                ? 'text-red-600 font-medium'
                                                : 'text-gray-600'
                                        }`}>
                                                {rowData.expirationText}
                                            </span>
                                    </div>
                                </td>

                                {/* Set Expiration */}
                                <td className="py-4 px-6">
                                    <input
                                        type="date"
                                        value={gameExpirations[rowData.game.id] || ''}
                                        onChange={(e) => handleExpirationChange(rowData.game.id, e.target.value)}
                                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </td>

                                {/* Actions */}
                                <td className="py-4 px-6">
                                    <div className="flex items-center space-x-2">
                                        {rowData.hasAccess && rowData.isActive ? (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleRevokeAccess(rowData.game.id)}
                                                isLoading={revokeAccess.isPending}
                                                className="flex items-center space-x-1"
                                            >
                                                <XCircle className="h-4 w-4"/>
                                                <span>Revoke</span>
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleGrantAccess(rowData.game.id)}
                                                isLoading={grantAccess.isPending}
                                                className="flex items-center space-x-1"
                                            >
                                                <CheckCircle className="h-4 w-4"/>
                                                <span>Grant</span>
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {gameInstances.length === 0 && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <GamepadIcon className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                            <p className="text-gray-500">No games available</p>
                            <p className="text-sm text-gray-400 mt-1">
                                Contact your administrator to set up game instances
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Access Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5"/>
                    <div className="flex-1">
                        <h3 className="font-medium text-blue-900">Access Summary</h3>
                        <div className="mt-2 text-sm text-blue-800">
                            <p>• <strong>{gameRowsData.filter(row => row.isActive).length}</strong> games currently
                                accessible</p>
                            <p>• <strong>{gameRowsData.filter(row => row.hasAccess && !row.isActive).length}</strong> expired
                                permissions</p>
                            <p>• <strong>{gameRowsData.filter(row => !row.hasAccess).length}</strong> games without
                                access</p>
                        </div>
                        <p className="mt-2 text-xs text-blue-600">
                            Changes take effect immediately. The host will be able to create game sessions for
                            accessible games.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
